import { Translations, UseI18n } from '~/app/i18n/i18n';
import {
	createCombinedLabelAndValue,
	createSingleLabelValue,
	createSingleLabelValueWithRemark,
} from '~/app/utils/create-combined-label-value';
import getTranslatedLabel from '~/app/utils/get-tranlslated-label';
import {
	brakeSystemCollection,
	consumptionCollection,
	drivelineCollection,
	emissionCollection,
	emissionCollectionPHEV,
	engineCollection,
	insuranceCollection,
	landingGearCollection,
	noiseCollection,
	performanceCollectionCombustion,
	performanceCollectionElectric,
	performanceCollectionHybrid,
	roofLoadCollection,
	steeringSystemCollection,
	technicalDataCollectionCombustion,
	technicalDataCollectionElectric,
	technicalDataCollectionHybrid,
	tongueLoadCollection,
	trailerWeightCollection,
	volumeCollection,
	weightsCollection,
} from '~/app/utils/technical-data-collections';
import {
	ConfiguredCarTechnicalDataQuery,
	MeasurementByEnergyManagement,
	ModelTechnicalDataQuery,
} from '~/graphql/__generated__/graphql';

function getFuelTypeTranslation(fuelType: string, i18n: UseI18n) {
	switch (fuelType) {
		case 'DIESEL':
			return i18n('ngc.technicalData.fuelType.diesel');
		case 'PETROL':
			return i18n('ngc.technicalData.fuelType.petrol');
		case 'ELECTRIC':
		case 'ELECTRICAL':
			return i18n('ngc.technicalData.fuelType.power');
		default:
			return '';
	}
}

export function getTechnicalDataItemsModel(
	td: NonNullable<
		NonNullable<
			ModelTechnicalDataQuery['configuredCar']['carlineStructureCarline']
		>['trimlines'][0]['models'][0]['technicalDataV2']
	>,
	market: string,
	driveTrain: string,
	i18n: UseI18n,
	translations: Translations,
) {
	const engineData = td.items.find((item) => engineCollection.includes(item.key));
	const performanceData =
		driveTrain === 'PHEV'
			? td.items.filter((item) => performanceCollectionHybrid.includes(item.key))
			: driveTrain === 'BEV'
				? td.items.filter((item) => performanceCollectionElectric.includes(item.key))
				: td.items.filter((item) => performanceCollectionCombustion.includes(item.key));
	const technicalData =
		driveTrain === 'PHEV'
			? td.items.filter((item) => technicalDataCollectionHybrid.includes(item.key))
			: driveTrain === 'BEV'
				? td.items.filter((item) => technicalDataCollectionElectric.includes(item.key))
				: td.items.filter((item) => technicalDataCollectionCombustion.includes(item.key));
	const drivelineData = td.items.filter((item) => drivelineCollection.includes(item.key));
	const landingGearData = td.items.filter((item) => landingGearCollection.includes(item.key));
	const brakeSystemData = td.items.filter((item) => brakeSystemCollection.includes(item.key));
	const steeringSystemData = td.items.filter((item) => steeringSystemCollection.includes(item.key));
	const weightsData = td.items.filter((item) => weightsCollection.includes(item.key));

	const roofLoad = td.items.filter((item) => roofLoadCollection.includes(item.key));
	const tongueWeight = td.items.filter((item) => tongueLoadCollection.includes(item.key));

	const trailerWeightData = td.items.filter((item) => trailerWeightCollection.includes(item.key));

	const volumeData = td.items.filter((item) => volumeCollection.includes(item.key));

	const noiseData = td.items.filter((item) => noiseCollection.includes(item.key));

	const consumptionData = td.items.filter((item) => consumptionCollection.includes(item.key));

	const insuranceData = td.items.filter((item) => insuranceCollection.includes(item.key));
	const emissionData =
		driveTrain === 'PHEV'
			? td.items.filter((item) => emissionCollectionPHEV.includes(item.key))
			: td.items.filter((item) => emissionCollection.includes(item.key));

	const fuelType =
		td?.consumptionAndEmission?.measurements?.[0]?.fuelType &&
		createSingleLabelValue({
			label: i18n('ngc.technicalData.consumptionAndEmission.fuelType'),
			formattedValue: td.consumptionAndEmission?.measurements
				?.map((measurement) => getFuelTypeTranslation(measurement?.fuelType, i18n))
				.join(' - '),
		});

	if (fuelType) {
		// @ts-ignore
		emissionData?.push(fuelType);
	}

	const roofToungeLoads =
		market !== 'TW' &&
		roofLoad &&
		roofLoad.length > 0 &&
		tongueWeight &&
		tongueWeight.length > 0 &&
		createCombinedLabelAndValue(
			{
				label: getTranslatedLabel(translations, roofLoad[0]?.item?.translationKey, roofLoad[0]?.item?.label),
				formattedValue: roofLoad[0]?.item?.formattedValue,
			},
			{
				label: getTranslatedLabel(
					translations,
					tongueWeight[0]?.item?.translationKey,
					tongueWeight[0]?.item?.label,
				),
				formattedValue: tongueWeight[0]?.item?.formattedValue,
			},
		);

	if (roofToungeLoads) {
		// @ts-ignore
		weightsData?.push(roofToungeLoads);
	}

	return {
		engineData,
		performanceData,
		technicalData,
		drivelineData,
		landingGearData,
		brakeSystemData,
		steeringSystemData,
		weightsData,
		roofLoad,
		tongueWeight,
		trailerWeightData,
		volumeData,
		consumptionData,
		noiseData,
		insuranceData,
		emissionData,
	};
}

export function getTechnicalDataItemsConfiguredCar(
	td: NonNullable<ConfiguredCarTechnicalDataQuery['configuredCar']['technicalDataV2']>,
	market: string,
	driveTrain: string,
	i18n: UseI18n,
	translations: Translations,
) {
	const engineData = td.items.find((item) => engineCollection.includes(item.key));
	const performanceData =
		driveTrain === 'PHEV'
			? td.items.filter((item) => performanceCollectionHybrid.includes(item.key))
			: driveTrain === 'BEV'
				? td.items.filter((item) => performanceCollectionElectric.includes(item.key))
				: td.items.filter((item) => performanceCollectionCombustion.includes(item.key));
	const technicalData =
		driveTrain === 'PHEV'
			? td.items.filter((item) => technicalDataCollectionHybrid.includes(item.key))
			: driveTrain === 'BEV'
				? td.items.filter((item) => technicalDataCollectionElectric.includes(item.key))
				: td.items.filter((item) => technicalDataCollectionCombustion.includes(item.key));
	const drivelineData = td.items.filter((item) => drivelineCollection.includes(item.key));
	const landingGearData = td.items.filter((item) => landingGearCollection.includes(item.key));
	const brakeSystemData = td.items.filter((item) => brakeSystemCollection.includes(item.key));
	const steeringSystemData = td.items.filter((item) => steeringSystemCollection.includes(item.key));
	const weightsData = td.items.filter((item) => weightsCollection.includes(item.key));

	const roofLoad = td.items.filter((item) => roofLoadCollection.includes(item.key));
	const tongueWeight = td.items.filter((item) => tongueLoadCollection.includes(item.key));

	const trailerWeightData = td.items.filter((item) => trailerWeightCollection.includes(item.key));

	const volumeData = td.items.filter((item) => volumeCollection.includes(item.key));

	const noiseData = td.items.filter((item) => noiseCollection.includes(item.key));

	const consumptionData = td.items.filter((item) => consumptionCollection.includes(item.key));

	const insuranceData = td.items.filter((item) => insuranceCollection.includes(item.key));
	const emissionData =
		driveTrain === 'PHEV'
			? td.items.filter((item) => emissionCollectionPHEV.includes(item.key))
			: td.items.filter((item) => emissionCollection.includes(item.key));

	const fuelType =
		td?.consumptionAndEmission?.measurements?.[0]?.fuelType &&
		createSingleLabelValue({
			label: i18n('ngc.technicalData.consumptionAndEmission.fuelType'),
			formattedValue: td.consumptionAndEmission?.measurements
				?.map((measurement) => getFuelTypeTranslation(measurement?.fuelType, i18n))
				.join(' - '),
		});

	if (fuelType) {
		// @ts-ignore
		emissionData.push(fuelType);
	}

	const measurements =
		market.toUpperCase() !== 'DE'
			? (td.consumptionAndEmission?.measurements[0]?.consumption as MeasurementByEnergyManagement[])
			: null;

	if (measurements && measurements.length > 0) {
		measurements.forEach((measurement) => {
			if (measurement.phases?.low) {
				emissionData.push(
					// @ts-ignore
					createSingleLabelValueWithRemark({
						label: getTranslatedLabel(
							translations,
							measurement.phases.low.translationKey,
							measurement.phases.low.label,
						),
						formattedValue: measurement.phases.low.formattedValue!,
						remarks: measurement.phases.low.remarks,
					}),
				);
			}
			if (measurement.phases?.medium) {
				emissionData.push(
					// @ts-ignore
					createSingleLabelValueWithRemark({
						label: getTranslatedLabel(
							translations,
							measurement.phases.medium.translationKey,
							measurement.phases.medium.label,
						),
						formattedValue: measurement.phases.medium.formattedValue!,
						remarks: measurement.phases.medium.remarks,
					}),
				);
			}
			if (measurement.phases?.high) {
				emissionData.push(
					// @ts-ignore
					createSingleLabelValueWithRemark({
						label: getTranslatedLabel(
							translations,
							measurement.phases.high.translationKey,
							measurement.phases.high.label,
						),
						formattedValue: measurement.phases.high.formattedValue!,
						remarks: measurement.phases.high.remarks,
					}),
				);
			}
			if (measurement.phases?.extraHigh) {
				emissionData.push(
					// @ts-ignore
					createSingleLabelValueWithRemark({
						label: getTranslatedLabel(
							translations,
							measurement.phases.extraHigh.translationKey,
							measurement.phases.extraHigh.label,
						),
						formattedValue: measurement.phases.extraHigh.formattedValue!,
						remarks: measurement.phases.extraHigh.remarks,
					}),
				);
			}
			if (measurement.phases?.city) {
				emissionData.push(
					// @ts-ignore
					createSingleLabelValueWithRemark({
						label: getTranslatedLabel(
							translations,
							measurement.phases.city.translationKey,
							measurement.phases.city.label,
						),
						formattedValue: measurement.phases.city.formattedValue!,
						remarks: measurement.phases.city.remarks,
					}),
				);
			}
		});
	}

	const roofToungeLoads =
		market.toLocaleUpperCase() !== 'TW' &&
		roofLoad &&
		roofLoad.length > 0 &&
		tongueWeight &&
		tongueWeight.length > 0 &&
		createCombinedLabelAndValue(
			{
				label: getTranslatedLabel(translations, roofLoad[0].item.translationKey, roofLoad[0].item.label),
				formattedValue: roofLoad[0].item.formattedValue,
			},
			{
				label: getTranslatedLabel(
					translations,
					tongueWeight[0].item.translationKey,
					tongueWeight[0].item.label,
				),
				formattedValue: tongueWeight[0].item.formattedValue,
			},
		);

	if (roofToungeLoads) {
		// @ts-ignore
		weightsData.push(roofToungeLoads);
	}

	return {
		engineData,
		performanceData,
		technicalData,
		drivelineData,
		landingGearData,
		brakeSystemData,
		steeringSystemData,
		weightsData,
		roofLoad,
		tongueWeight,
		trailerWeightData,
		volumeData,
		consumptionData,
		noiseData,
		insuranceData,
		emissionData,
	};
}
